import { FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsDimensions, AnalyticsEvent, WidgetEventDimensions, logAnalyticsEvent } from '@vestico/utils';

import { ProductInfo, ProductInfoMapping, ProductResponse, ProductWidget, UserDocumentID } from '@vestico/model';

import { CommonAppContext, CustomFontContextProvider, UserContext } from '@vestico/shared';

import { MatchingAPIContext, ProductAPIContext, ProductContext, ProductContextProps, VisitsAPIContext, WidgetAuthContext } from './MainContext';
import { UserInteractionsProvider } from './UserInteractionsProvider';
import { UserStateContext } from './UserStateProvider';
import { WidgetEntryHandler } from './WidgetEntryHandler';
import { ConfigContext, MatchButtonGlobalsContext } from './WidgetEventsManager';
import { ProductConfiguration, ProductResponseRetry, getProductConfigUIDs } from './model/api';
import { MatchingApiClient } from './services/apiClientMatching';
import { ProductApiClient } from './services/apiClientProduct';
import { VisitsApiClient } from './services/apiClientVisits';

export const MainContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const {
    isPreview,
    styles,
    clientId,
    products: productsConfig,
    isShowcase,
    iOSBundleIdentifier,
    sdkAPIKey,
    experiments,
  } = useContext(ConfigContext);

  const productAPI = useRef(new ProductApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments));
  const visitsAPI = useRef(new VisitsApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments));
  const matchingAPI = useRef(new MatchingApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments));

  const productConfigRef = useRef<ProductConfiguration[] | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [activeProduct, setActiveProduct] = useState<ProductWidget | null | undefined>(undefined);
  const [activeProductUID, setActiveProductUID] = useState<string | null | undefined>(undefined);
  const [productsInfo, setProductsInfo] = useState<ProductInfoMapping | null | undefined>(undefined);

  const { authInfo, resetAuth } = useContext(WidgetAuthContext);
  const productRefetchCounter = useRef(0);

  const [widgetStyles, setWidgetStyles] = useState(styles);

  useLayoutEffect(() => {
    productAPI.current = new ProductApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments);
    visitsAPI.current = new VisitsApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments);
    matchingAPI.current = new MatchingApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments);
  }, [clientId, experiments, iOSBundleIdentifier, isPreview, sdkAPIKey]);

  // User State

  const { didUpdateProduct, updateUserUID } = useContext(UserStateContext);
  const { uid: userUID } = useContext(UserContext);

  const { matchButtonOpen, entryIdentifier: widgetEntryIdentifier, setCanShowMatchButton } = useContext(MatchButtonGlobalsContext);
  const [internalFitMatchOpen, setInternalFitMatchOpen] = useState(matchButtonOpen);

  const productPostsWithUserMatchInfo = useCallback((productInfo?: ProductInfo) => {
    const postsWithMatchInfo = productInfo ? productInfo.postsWithMatchInfo : 0;
    return postsWithMatchInfo;
  }, []);

  const activeProductPostsWithUserMatchInfo = useMemo(() => {
    if (!activeProduct || !productsInfo) {
      return 0;
    }
    const productInfo = productsInfo[activeProduct.uid];
    return productPostsWithUserMatchInfo(productInfo);
  }, [activeProduct, productsInfo, productPostsWithUserMatchInfo]);

  const activeProductPostsWithoutUserMatchInfo = useMemo(() => {
    if (!activeProduct || !productsInfo) {
      return 0;
    }
    const productInfo = productsInfo[activeProduct.uid];
    const postsWithoutMatchInfo = productInfo ? productInfo.postsWithoutMatchInfo : 0;
    return postsWithoutMatchInfo;
  }, [activeProduct, productsInfo]);

  const activeProductSizeRecommendationAvailable = useMemo(() => {
    if (!activeProduct || !productsInfo) {
      return false;
    }
    const productInfo = productsInfo[activeProduct.uid];
    return !!productInfo?.sizeRecommendationAvailable;
  }, [activeProduct, productsInfo]);

  const activeProductSizeChart = useMemo(() => {
    if (!activeProduct || !productsInfo) {
      return undefined;
    }
    const productInfo = productsInfo[activeProduct.uid];
    return productInfo?.sizeChart;
  }, [activeProduct, productsInfo]);

  const activeProductUnmatchable = useMemo(() => {
    if (!activeProduct || !productsInfo) {
      return false;
    }
    const productInfo = productsInfo[activeProduct.uid];
    return !!productInfo?.unmatchable;
  }, [activeProduct, productsInfo]);

  useEffect(() => {
    didUpdateProduct(activeProduct);
  }, [activeProduct, didUpdateProduct]);

  useEffect(() => {
    // record close event
    if (internalFitMatchOpen && !matchButtonOpen && activeProduct) {
      const dimensions: WidgetEventDimensions = {
        item_id: activeProduct.documentID,
        name: activeProduct.name,
        brand: activeProduct.brandName,
        provider: clientId,
      };
      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.close, dimensions });
    }

    setInternalFitMatchOpen(matchButtonOpen);
  }, [matchButtonOpen]);

  useEffect(() => {
    if (matchButtonOpen && activeProduct) {
      const dimensions: WidgetEventDimensions = {
        item_id: activeProduct.documentID,
        name: activeProduct.name,
        brand: activeProduct.brandName,
        provider: clientId,
        entry_id: widgetEntryIdentifier,
      };
      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.open, dimensions });
    }
  }, [matchButtonOpen, activeProduct, clientId, widgetEntryIdentifier]);

  useEffect(() => {
    if (!authInfo) {
      setActiveProduct(null);
    }
  }, [authInfo]);

  const processProductResponse = useCallback(
    (fetchedProductResponse: ProductResponse | 'retry' | null) => {
      if (!productsConfig || !productConfigRef.current) {
        console.error(`Expecting product config.`);
        return;
      }

      // force refresh auth info
      if (fetchedProductResponse === ProductResponseRetry) {
        productRefetchCounter.current += 1;
        resetAuth();
        return;
      }

      // Compare if we're still trying to set the same
      // Ignore if they've changed
      const requestProductUIDs = productsConfig?.map((p) => p.productUID) ?? [];
      const currentProductUIDs = productConfigRef.current.map((p) => p.productUID);

      if (JSON.stringify(requestProductUIDs) !== JSON.stringify(currentProductUIDs)) {
        console.info(`Ignoring product callback for ${requestProductUIDs.join(', ')}`);
        return;
      }

      const fetchedProductsInfo = fetchedProductResponse?.productsInfo ?? null;
      const fetchedProductUIDs = fetchedProductsInfo ? Object.keys(fetchedProductsInfo) : null;

      const fetchedActiveProductUID = fetchedProductUIDs && fetchedProductUIDs.length > 0 ? fetchedProductUIDs[0] : null;

      setProductsInfo(fetchedProductsInfo);
      setActiveProductUID(fetchedActiveProductUID);

      // Update set can show match immediately
      const productInfo = fetchedProductsInfo && fetchedActiveProductUID ? fetchedProductsInfo[fetchedActiveProductUID] : undefined;
      const activePostsWithUserMatchInfo = fetchedProductsInfo && fetchedActiveProductUID ? productPostsWithUserMatchInfo(productInfo) : 0;
      setCanShowMatchButton(requestProductUIDs[0], activePostsWithUserMatchInfo > 0, !!productInfo?.sizeRecommendationAvailable);

      const newStyles = fetchedProductResponse?.styles;
      if (newStyles) {
        setWidgetStyles(newStyles);
      }

      if (fetchedProductsInfo) {
        console.debug(
          `finished loading products "${Object.values(fetchedProductsInfo).map(
            (productInfo) => `${productInfo.product.name}: ${productInfo.product.documentID}`
          )}"`
        );
      } else {
        console.info(`product fetch failed ${requestProductUIDs.join(',')}, client ${clientId}`);
      }

      setLoading(false);
    },
    [clientId, productsConfig, productPostsWithUserMatchInfo, resetAuth, setCanShowMatchButton]
  );

  const loadProductIfNeeded = useCallback(
    async (force?: boolean) => {
      if (loading && !force) {
        return;
      }

      if (activeProductUID !== undefined && !force) {
        setLoading(false);
        return;
      }

      if (!authInfo || !userUID) {
        return;
      }

      // Compare if we're still trying to set the same products
      // Ignore if they've changed
      if (productConfigRef.current && productsConfig) {
        const currentProductUIDs = productConfigRef.current.map((p) => p.productUID);
        const requestProductUIDs = productsConfig.map((p) => p.productUID);
        if (JSON.stringify(currentProductUIDs) === JSON.stringify(requestProductUIDs)) {
          console.debug(`Product UIDs didn't change`);
          return;
        }
      }

      if (force) {
        setProductsInfo(undefined);
        productRefetchCounter.current = 0;
      }

      setLoading(true);

      if (productsConfig === undefined) {
        setLoading(false);
        return;
      }

      console.debug(`loading product ${getProductConfigUIDs(productsConfig)}, ${clientId}`);

      // Set the current product config ref just before the call is made
      productConfigRef.current = productsConfig;

      const fetchedProductResponse = await productAPI.current.fetchProduct(
        productsConfig,
        userUID,
        { authInfo, resetAuth },
        productRefetchCounter.current === 0
      );
      processProductResponse(fetchedProductResponse);
    },
    [loading, activeProductUID, userUID, authInfo, productsConfig, clientId, resetAuth, processProductResponse]
  );

  const loadStylesIfNeeded = useCallback(async () => {
    if (loading || productsConfig !== undefined || !authInfo) {
      return;
    }

    setLoading(true);

    console.debug(`Loading styles: ${clientId}`);

    const fetchedWidgetStylesResponse = await productAPI.current.fetchWidgetStyles({ authInfo, resetAuth });

    if (fetchedWidgetStylesResponse) {
      console.debug('Finished loading widget styles');
      setWidgetStyles(fetchedWidgetStylesResponse);
    } else {
      console.error(`Widget styles fetch failed: client ${clientId}`);
    }

    setLoading(false);
  }, [loading, productsConfig, authInfo, clientId, resetAuth]);

  const recordedLoadEventUserDocID = useRef<UserDocumentID | undefined>(undefined);

  const getUserUID = useCallback(async () => {
    // Don't record again if we're already pushed the event to the server
    if (!authInfo || (recordedLoadEventUserDocID.current && userUID === recordedLoadEventUserDocID.current)) {
      return;
    }

    // We're using load events for globally understanding which user has been recorded
    const userDocumentID = await visitsAPI.current.recordLoadEvent(productsConfig, userUID, isShowcase, { authInfo, resetAuth });
    if (userDocumentID) {
      recordedLoadEventUserDocID.current = userDocumentID;
      console.debug(`Setting assigned user document id ${userDocumentID}`);
      updateUserUID(userDocumentID);
    }
  }, [productsConfig, authInfo, userUID, resetAuth, updateUserUID, isShowcase]);

  useLayoutEffect(() => {
    getUserUID();
  }, [getUserUID]);

  // show/hide main widget button - for single product pages
  useEffect(() => {
    if (loading || !productsInfo || Object.keys(productsInfo).length > 1) {
      return;
    }

    const dimensions: AnalyticsDimensions = {
      provider: clientId,
    };

    let hasPosts = false;
    if (activeProductUID && productsInfo) {
      const productInfo = productsInfo[activeProductUID];
      if (productInfo) {
        hasPosts = productInfo.postsWithMatchInfo > 0;

        const { product } = productInfo;
        const widgetParams: WidgetEventDimensions = dimensions as WidgetEventDimensions;
        widgetParams.item_id = product.documentID;
        widgetParams.name = product.name;
        widgetParams.brand = product.brandName;
      }
    }

    if (hasPosts) {
      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.loadHasPosts, dimensions });
    }
  }, [activeProduct]);

  // show/hide multi products page widget button
  useEffect(() => {
    if (loading || !productsInfo || Object.keys(productsInfo).length < 1) {
      return;
    }

    Object.values(productsInfo).forEach((productInfo) => {
      let hastPostsMultiProduct = false;
      if (productInfo) {
        hastPostsMultiProduct = hastPostsMultiProduct || productInfo.postsWithMatchInfo > 0;
        console.debug(`product ${productInfo.product.name}. hasPosts ${hastPostsMultiProduct}`);
      }

      // Record load events after everything else
      const dimensions: AnalyticsDimensions = {
        provider: clientId,
      };

      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.load, dimensions });
      if (hastPostsMultiProduct) {
        logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.loatHasPostsMultiProducts, dimensions });
      }
    });
  }, [productsInfo, loading]);

  useEffect(() => {
    // either of them will be loaded
    loadProductIfNeeded();
    loadStylesIfNeeded();
  }, [authInfo, userUID]);

  useEffect(() => {
    loadProductIfNeeded(true);
  }, [productsConfig]);

  useEffect(() => {
    if (!activeProductUID) {
      setActiveProduct(undefined);
      return;
    }

    if (!productsInfo) {
      return;
    }

    const productInfo = productsInfo[activeProductUID];
    if (productInfo) {
      setActiveProduct(productInfo.product);
    } else {
      setActiveProduct(null);
    }
  }, [activeProductUID]);

  const productContextValue: ProductContextProps = useMemo(() => {
    const anyProductHasSizeRecAvailable = Object.values(productsInfo ?? {}).some((productInfo) => productInfo?.sizeRecommendationAvailable);
    const defaultProductUID = productsConfig?.find((config) => !config.isTag)?.productUID;

    return {
      product: activeProduct,
      postsWithUserMatchInfo: activeProductPostsWithUserMatchInfo,
      postsWithoutUserMatchInfo: activeProductPostsWithoutUserMatchInfo,
      sizeRecommendationAvailable: activeProductSizeRecommendationAvailable,
      sizeChart: activeProductSizeChart,
      unmatchable: activeProductUnmatchable,
      selectedVariantUID: productsConfig?.[0]?.variant,
      allProducts: productsInfo,
      defaultProduct: defaultProductUID && productsInfo ? productsInfo[defaultProductUID]?.product : undefined,
      anyProductHasSizeRecommendationAvailable: anyProductHasSizeRecAvailable,
      onSetActiveProduct: setActiveProductUID,
    };
  }, [
    activeProduct,
    activeProductPostsWithUserMatchInfo,
    activeProductPostsWithoutUserMatchInfo,
    activeProductSizeChart,
    activeProductSizeRecommendationAvailable,
    activeProductUnmatchable,
    productsConfig,
    productsInfo,
  ]);

  return (
    <CommonAppContext styles={widgetStyles}>
      <ProductAPIContext.Provider value={productAPI.current}>
        <VisitsAPIContext.Provider value={visitsAPI.current}>
          <MatchingAPIContext.Provider value={matchingAPI.current}>
            <ProductContext.Provider value={productContextValue}>
              <UserInteractionsProvider>
                <CustomFontContextProvider>
                  <WidgetEntryHandler entries={widgetStyles?.entries}>{children}</WidgetEntryHandler>
                </CustomFontContextProvider>
              </UserInteractionsProvider>
            </ProductContext.Provider>
          </MatchingAPIContext.Provider>
        </VisitsAPIContext.Provider>
      </ProductAPIContext.Provider>
    </CommonAppContext>
  );
};
